import { useEffect, useState } from "react"

export interface UseLiveRemainingMsOptions {
  /** Dev / fixture: fixed timeline clock (ms). When set, `clockOffsetMs` is ignored. */
  nowMs?: number | null
  /** From latest realtime push — align `Date.now()` with server for `endsAt − now`. */
  clockOffsetMs?: number
}

/**
 * Live countdown to `endsAtMs` while `active`. Uses server clock offset when provided so
 * progress rings match WS-driven phase transitions (device clock skew / Telegram WebView).
 */
export function useLiveRemainingMs(
  endsAtMs: number | null,
  active: boolean,
  options?: UseLiveRemainingMsOptions
): number | null {
  const nowMs = options?.nowMs
  const hasExternalNow = nowMs != null && Number.isFinite(nowMs)
  const clockOffsetMs = hasExternalNow ? 0 : (options?.clockOffsetMs ?? 0)

  const [remaining, setRemaining] = useState<number | null>(() =>
    endsAtMs != null
      ? Math.max(0, endsAtMs - (Date.now() + clockOffsetMs))
      : null
  )

  useEffect(() => {
    if (!active || endsAtMs == null) {
      setRemaining(null)
      return
    }

    const alignedNow = () =>
      (hasExternalNow ? (nowMs as number) : Date.now()) +
      (hasExternalNow ? 0 : clockOffsetMs)

    const tick = () => setRemaining(Math.max(0, endsAtMs - alignedNow()))
    tick()

    if (hasExternalNow) {
      return
    }

    const id = setInterval(tick, 64)
    return () => clearInterval(id)
  }, [active, endsAtMs, hasExternalNow, nowMs, clockOffsetMs])

  return remaining
}
